import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { translations, de, en } from '../i18n/translations';
import { Language, TranslationDictionary } from '../i18n/types';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationDictionary & ((path: string) => string);
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem('cad_language');
      return (saved as Language) || 'de';
    } catch (e) {
      console.warn('Failed to read language from localStorage:', e);
      return 'de';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('cad_language', language);
      document.documentElement.lang = language;
    } catch (e) {
      console.warn('Failed to save language to localStorage:', e);
    }
  }, [language]);

  const translate = (path: string): string => {
    const keys = path.split('.');
    let current: any = translations[language];
    
    for (const key of keys) {
      if (current === undefined || current[key] === undefined) {
        // Fallback to German if missing
        let fallback: any = translations['de'];
        let foundInFallback = true;
        for (const fbKey of keys) {
          if (fallback && fallback[fbKey]) {
            fallback = fallback[fbKey];
          } else {
            foundInFallback = false;
            break;
          }
        }
        if (foundInFallback && typeof fallback === 'string') {
          return fallback;
        }
        
        // Log warning only in development
        if (import.meta.env.DEV) {
          console.warn(`Translation missing for key: ${path} in language: ${language}`);
        }
        return path;
      }
      current = current[key];
    }
    
    return typeof current === 'string' ? current : path;
  };

  // Create a proxy to allow both function calls t('path') and object access t.section.key
  // We memoize the proxy to avoid creating a new one on every render if possible, 
  // but since it depends on 'language' (via closure in translate), we recreate it.
  const t = new Proxy(translate, {
    get: (target, prop: string | symbol) => {
      // If property exists on function (like call, apply, bind, prototype), return it
      if (prop in target) {
        return (target as any)[prop];
      }
      // Otherwise return from translation dictionary
      if (translations[language] && (prop in translations[language])) {
        return (translations[language] as any)[prop];
      }
      // Fallback for property access
      if (translations['de'] && (prop in translations['de'])) {
        return (translations['de'] as any)[prop];
      }
      return undefined;
    }
  }) as TranslationDictionary & ((path: string) => string);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

// Fallback t function for when context is missing
const fallbackT = new Proxy(((path: string) => path), {
  get: (target, prop: string | symbol) => {
    if (prop in target) return (target as any)[prop];
    // Return empty object proxy for nested properties to prevent crashes like t.dialogs.title
    return new Proxy({}, {
      get: () => ""
    });
  }
}) as any;

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    console.error('❌ useLanguage called outside LanguageProvider. Returning fallback context.');
    // Return a safe fallback instead of throwing to prevent app crash
    return {
      language: 'de' as Language,
      setLanguage: () => console.warn('Cannot set language outside provider'),
      t: fallbackT
    };
  }
  return context;
}
